import { api } from './api';

const GRANTS_BASE_PATH = '/v1/grants';
export const GRANT_NOTIFICATIONS_READ_EVENT = 'govify:grant-notifications-read';

const NOTIFICATION_RESPONSE_KEYS = ['count', 'unread'];

const normalizeGrantNotificationStatus = (response, expectedRead = null) => {
  if (
    !isObject(response) ||
    Object.keys(response).sort().join(',') !==
      NOTIFICATION_RESPONSE_KEYS.join(',') ||
    typeof response.unread !== 'boolean' ||
    !Number.isInteger(response.count) ||
    response.count < 0 ||
    response.unread !== response.count > 0 ||
    (expectedRead !== null && response.unread !== expectedRead)
  ) {
    throw new Error('The grants notification response was invalid.');
  }

  return {
    unread: response.unread,
    count: response.count,
  };
};

const isObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const nullableString = (value) => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const nullableNumber = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const metadataValue = (value) => {
  if (typeof value === 'string') return nullableString(value);
  if (typeof value === 'number') return nullableNumber(value);
  if (typeof value === 'boolean' || value === null) return value;
  if (Array.isArray(value)) return value.map(metadataValue);
  if (isObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, metadataValue(entry)]),
    );
  }
  return null;
};

const nullableObject = (value) => (isObject(value) ? value : {});
const applicationDetailFileKinds = new Set([
  'sponsorship',
  'application',
  'certification',
  'additional_document',
  'questions',
  'answer',
]);

const normalizeListPage = (value) => {
  const source = nullableObject(value);
  if (Object.prototype.hasOwnProperty.call(source, 'nextCursor')) {
    return { nextCursor: nullableString(source.nextCursor) };
  }
  const pageNumber = nullableNumber(source.pageNumber);
  const pageSize = nullableNumber(source.pageSize);
  const totalItems = nullableNumber(source.totalItems);
  const totalPages = nullableNumber(source.totalPages);
  if (
    !Number.isSafeInteger(pageNumber) ||
    pageNumber < 1 ||
    !Number.isSafeInteger(pageSize) ||
    pageSize < 1 ||
    !Number.isSafeInteger(totalItems) ||
    totalItems < 0 ||
    !Number.isSafeInteger(totalPages) ||
    totalPages < 0 ||
    totalPages !== Math.ceil(totalItems / pageSize) ||
    (totalPages === 0 ? pageNumber !== 1 : pageNumber > totalPages)
  ) {
    throw new Error('The grants pagination response was invalid.');
  }
  return { pageNumber, pageSize, totalItems, totalPages };
};

const normalizeCycle = (cycle) => {
  const source = nullableObject(cycle);
  return {
    key: nullableString(source.key),
    title: nullableString(source.title),
    status: nullableString(source.status),
    sponsorshipOpensAt: nullableString(source.sponsorshipOpensAt),
    sponsorshipClosesAt: nullableString(source.sponsorshipClosesAt),
    applicationClosesAt: nullableString(source.applicationClosesAt),
  };
};

export const normalizeGrantCycles = (response) => {
  const source = assertObjectResponse(response, 'cycles');
  const currentCycleKey = nullableString(source.currentCycleKey);
  const items = Array.isArray(source.items)
    ? source.items.map(normalizeCycle)
    : [];
  if (
    source.apiVersion !== '1' ||
    !currentCycleKey ||
    items.length === 0 ||
    items.some((cycle) => !cycle.key || !cycle.title || !cycle.status) ||
    !items.some((cycle) => cycle.key === currentCycleKey)
  ) {
    throw new Error('The grants cycles response was invalid.');
  }
  return {
    apiVersion: source.apiVersion,
    currentCycleKey,
    items,
  };
};

const normalizeSummary = (application) => {
  const source = nullableObject(application);
  const applicant = nullableObject(source.applicant);
  const project = nullableObject(source.project);
  const sponsorship = nullableObject(source.sponsorship);
  const submission = nullableObject(source.application);
  const workshop = nullableObject(source.preAppWorkshop);
  const questions = nullableObject(source.questions);
  const answer = nullableObject(source.answer);
  const staffTracking = nullableObject(source.staffTracking);

  return {
    id: nullableString(source.id),
    organizationId: nullableString(source.organizationId),
    manualVersion: nullableNumber(source.manualVersion) ?? 0,
    source:
      source.source === 'staff_manual' ? 'staff_manual' : 'applicant_portal',
    organization: nullableString(source.organization),
    projectName: nullableString(source.projectName),
    applicant: {
      id: nullableString(applicant.id),
      firstName: nullableString(applicant.firstName),
      lastName: nullableString(applicant.lastName),
      email: nullableString(applicant.email),
      phoneNumber: nullableString(applicant.phoneNumber),
    },
    project: {
      address: nullableString(project.address),
      description: nullableString(project.description),
      requestedAmount: nullableString(project.requestedAmount),
    },
    sponsorship: {
      status: nullableString(sponsorship.status),
      type: nullableString(sponsorship.type),
      coApplicant: nullableString(sponsorship.coApplicant),
      submittedAt: nullableString(sponsorship.submittedAt),
      scanStatus: nullableString(sponsorship.scanStatus),
    },
    application: {
      submittedAt: nullableString(submission.submittedAt),
      scanStatus: nullableString(submission.scanStatus),
    },
    preAppWorkshop: {
      attended:
        typeof workshop.attended === 'boolean' ? workshop.attended : null,
      version: nullableNumber(workshop.version) ?? 0,
      updatedAt: nullableString(workshop.updatedAt),
    },
    staffTracking: {
      proposedAwardAmount: nullableString(staffTracking.proposedAwardAmount),
      awardedAmount: nullableString(staffTracking.awardedAmount),
      priorities: nullableString(staffTracking.priorities),
      notes: nullableString(staffTracking.notes),
    },
    questions: source.questions
      ? {
          id: nullableString(questions.id),
          fileName: nullableString(questions.fileName),
          contentType: nullableString(questions.contentType),
          sizeBytes: nullableNumber(questions.sizeBytes),
          submittedAt: nullableString(questions.submittedAt),
          scanStatus: nullableString(questions.scanStatus),
        }
      : null,
    answer: source.answer
      ? {
          id: nullableString(answer.id),
          fileName: nullableString(answer.fileName),
          contentType: nullableString(answer.contentType),
          sizeBytes: nullableNumber(answer.sizeBytes),
          submittedAt: nullableString(answer.submittedAt),
          scanStatus: nullableString(answer.scanStatus),
        }
      : null,
    createdAt: nullableString(source.createdAt),
    updatedAt: nullableString(source.updatedAt),
  };
};

const assertObjectResponse = (response, label) => {
  if (!isObject(response)) {
    throw new Error(`The grants ${label} response was invalid.`);
  }
  return response;
};

export const normalizeGrantsOverview = (response) => {
  const source = assertObjectResponse(response, 'overview');
  if (
    source.apiVersion !== '2' ||
    !isObject(source.cycle) ||
    !isObject(source.counts)
  ) {
    throw new Error('The grants overview response was invalid.');
  }
  const counts = source.counts;
  const organizations = nullableNumber(counts.organizations);
  const sponsorshipLettersUploaded = nullableNumber(
    counts.sponsorshipLettersUploaded,
  );
  const applicationsSubmitted = nullableNumber(counts.applicationsSubmitted);
  if (
    !Number.isSafeInteger(organizations) ||
    organizations < 0 ||
    !Number.isSafeInteger(sponsorshipLettersUploaded) ||
    sponsorshipLettersUploaded < 0 ||
    !Number.isSafeInteger(applicationsSubmitted) ||
    applicationsSubmitted < 0
  ) {
    throw new Error('The grants overview response was invalid.');
  }

  return {
    apiVersion: source.apiVersion,
    cycle: normalizeCycle(source.cycle),
    counts: {
      organizations,
      sponsorshipLettersUploaded,
      applicationsSubmitted,
    },
  };
};

export const normalizeGrantsApplications = (response) => {
  const source = assertObjectResponse(response, 'applications');
  if (
    !isObject(source.cycle) ||
    !Array.isArray(source.items) ||
    !isObject(source.page)
  ) {
    throw new Error('The grants applications response was invalid.');
  }

  return {
    apiVersion: nullableString(source.apiVersion),
    cycle: normalizeCycle(source.cycle),
    items: source.items.map(normalizeSummary),
    page: normalizeListPage(source.page),
  };
};

export const normalizeGrantApplicationDetail = (response) => {
  const source = assertObjectResponse(response, 'application detail');
  if (!isObject(source.application)) {
    throw new Error('The grants application detail response was invalid.');
  }

  const rawApplication = source.application;
  const summary = normalizeSummary(rawApplication);
  const applicant = nullableObject(rawApplication.applicant);
  const sponsorship = nullableObject(rawApplication.sponsorship);
  const submission = nullableObject(rawApplication.submission);
  const files = Array.isArray(rawApplication.files) ? rawApplication.files : [];
  const presentations = Array.isArray(rawApplication.presentations)
    ? rawApplication.presentations
    : [];

  return {
    apiVersion: nullableString(source.apiVersion),
    application: {
      ...summary,
      applicant: {
        ...summary.applicant,
        company: nullableString(applicant.company),
      },
      sponsorship: {
        ...summary.sponsorship,
        applicantName: nullableString(sponsorship.applicantName),
        projectTitle: nullableString(sponsorship.projectTitle),
        grantAmountRequested: nullableString(sponsorship.grantAmountRequested),
        projectDescription: nullableString(sponsorship.projectDescription),
      },
      submission: {
        coverPage: isObject(submission.coverPage)
          ? metadataValue(submission.coverPage)
          : null,
        nonContingency: isObject(submission.nonContingency)
          ? metadataValue(submission.nonContingency)
          : null,
        certification: isObject(submission.certification)
          ? metadataValue(submission.certification)
          : null,
        submittedAt: nullableString(submission.submittedAt),
      },
      files: files.map((file) => {
        const normalizedFile = nullableObject(file);
        const fileId = nullableString(normalizedFile.id);
        if (!fileId) {
          throw new Error(
            'The grants application detail response was invalid.',
          );
        }
        const kind = nullableString(normalizedFile.kind);
        if (!applicationDetailFileKinds.has(kind)) {
          throw new Error(
            'The grants application detail response was invalid.',
          );
        }
        return {
          id: fileId,
          kind,
          fileName: nullableString(normalizedFile.fileName),
          description: nullableString(normalizedFile.description),
          contentType: nullableString(normalizedFile.contentType),
          sizeBytes: nullableNumber(normalizedFile.sizeBytes),
          uploadStatus: nullableString(normalizedFile.uploadStatus),
          scanStatus: nullableString(normalizedFile.scanStatus),
          submittedAt: nullableString(normalizedFile.submittedAt),
        };
      }),
      presentations: presentations.map((value) => {
        const presentation = nullableObject(value);
        const id = nullableString(presentation.id);
        if (!id) {
          throw new Error('The grants application detail response was invalid.');
        }
        return {
          id,
          fileId: nullableString(presentation.fileId),
          fileName: nullableString(presentation.fileName),
          details: nullableString(presentation.details),
          contentType: nullableString(presentation.contentType),
          sizeBytes: nullableNumber(presentation.sizeBytes),
          scanStatus: nullableString(presentation.scanStatus),
          submittedAt: nullableString(presentation.submittedAt),
        };
      }),
    },
  };
};

const normalizeSponsorshipWorkItem = (value) => {
  const source = nullableObject(value);
  const applicant = nullableObject(source.applicant);
  const file = nullableObject(source.file);
  return {
    id: nullableString(source.id),
    organizationId: nullableString(source.organizationId),
    submissionId: nullableString(source.submissionId),
    source:
      source.source === 'staff_manual' ? 'staff_manual' : 'applicant_portal',
    documentStatus: nullableString(source.documentStatus),
    accountName: nullableString(source.accountName),
    applicant: {
      id: nullableString(applicant.id),
      name: nullableString(applicant.name),
      contactName: nullableString(applicant.contactName),
      email: nullableString(applicant.email),
      phone: nullableString(applicant.phone),
      mailingAddress: nullableString(applicant.mailingAddress),
      contactTitle: nullableString(applicant.contactTitle),
    },
    coApplicant: nullableString(source.coApplicant),
    projectTitle: nullableString(source.projectTitle),
    grantAmountRequested: nullableString(source.grantAmountRequested),
    sponsorshipType: nullableString(source.sponsorshipType),
    projectDescription: nullableString(source.projectDescription),
    projectAddress: nullableString(source.projectAddress),
    workshopAttended:
      source.workshopAttended === true
        ? true
        : source.workshopAttended === false
          ? false
          : null,
    scanStatus: nullableString(source.scanStatus),
    submittedAt: nullableString(source.submittedAt),
    decision: nullableString(source.decision),
    decisionVersion: nullableNumber(source.decisionVersion),
    file: source.file
      ? {
          id: nullableString(file.id),
          fileName: nullableString(file.fileName),
          contentType: nullableString(file.contentType),
          sizeBytes: nullableNumber(file.sizeBytes),
        }
      : null,
  };
};

export const normalizeGrantSponsorships = (response) => {
  const source = assertObjectResponse(response, 'sponsorships');
  if (
    !isObject(source.cycle) ||
    !Array.isArray(source.items) ||
    !isObject(source.page)
  ) {
    throw new Error('The grants sponsorships response was invalid.');
  }
  return {
    apiVersion: nullableString(source.apiVersion),
    cycle: normalizeCycle(source.cycle),
    items: source.items.map(normalizeSponsorshipWorkItem),
    page: normalizeListPage(source.page),
  };
};

const normalizeSponsorshipDownload = (response) => {
  const source = assertObjectResponse(response, 'sponsorship download');
  const download = nullableObject(source.download);
  const file = nullableObject(source.file);
  const url = nullableString(download.url);
  if (
    source.status !== 'download_ready' ||
    !url ||
    !url.startsWith('https://')
  ) {
    throw new Error('The sponsorship download response was invalid.');
  }
  return {
    status: source.status,
    file: {
      id: nullableString(file.id),
      fileName: nullableString(file.fileName),
      contentType: nullableString(file.contentType),
      sizeBytes: nullableNumber(file.sizeBytes),
    },
    download: { url, expiresAt: nullableString(download.expiresAt) },
  };
};

const normalizeSponsorshipDecision = (response) => {
  const source = assertObjectResponse(response, 'sponsorship decision');
  const decision = nullableObject(source.decision);
  if (
    source.status !== 'decision_recorded' ||
    typeof source.duplicate !== 'boolean'
  ) {
    throw new Error('The sponsorship decision response was invalid.');
  }
  return {
    status: source.status,
    duplicate: source.duplicate,
    decision: {
      id: nullableString(decision.id),
      applicationId: nullableString(decision.applicationId),
      decision: nullableString(decision.decision),
      previousDecision: nullableString(decision.previousDecision),
      version: nullableNumber(decision.version),
      decidedAt: nullableString(decision.decidedAt),
    },
  };
};

const normalizeOrganizationSummary = (value) => {
  const source = nullableObject(value);
  return {
    id: nullableString(source.id),
    name: nullableString(source.name),
    mailingAddress: nullableString(source.mailingAddress),
    phone: nullableString(source.phone),
    version: nullableNumber(source.version) ?? 0,
    contactCount: nullableNumber(source.contactCount) ?? 0,
    grantCount: nullableNumber(source.grantCount) ?? 0,
    pendingSponsorshipCount:
      nullableNumber(source.pendingSponsorshipCount) ?? 0,
    updatedAt: nullableString(source.updatedAt),
  };
};

export const normalizeGrantOrganizations = (response) => {
  const source = assertObjectResponse(response, 'organizations');
  if (
    !isObject(source.cycle) ||
    !Array.isArray(source.items) ||
    !isObject(source.page)
  ) {
    throw new Error('The grants organizations response was invalid.');
  }
  return {
    apiVersion: nullableString(source.apiVersion),
    cycle: normalizeCycle(source.cycle),
    items: source.items.map(normalizeOrganizationSummary),
    page: normalizeListPage(source.page),
  };
};

export const normalizeGrantOrganizationWorkspace = (response) => {
  const source = assertObjectResponse(response, 'organization workspace');
  if (
    !isObject(source.cycle) ||
    !isObject(source.organization) ||
    !Array.isArray(source.contacts) ||
    !Array.isArray(source.grants) ||
    !Array.isArray(source.activity)
  ) {
    throw new Error('The grants organization workspace response was invalid.');
  }
  const organization = source.organization;
  return {
    apiVersion: nullableString(source.apiVersion),
    cycle: normalizeCycle(source.cycle),
    organization: {
      id: nullableString(organization.id),
      name: nullableString(organization.name),
      mailingAddress: nullableString(organization.mailingAddress),
      phone: nullableString(organization.phone),
      notes: nullableString(organization.notes),
      version: nullableNumber(organization.version) ?? 0,
      createdAt: nullableString(organization.createdAt),
      updatedAt: nullableString(organization.updatedAt),
    },
    contacts: source.contacts.map((value) => {
      const contact = nullableObject(value);
      return {
        id: nullableString(contact.id),
        firstName: nullableString(contact.firstName),
        lastName: nullableString(contact.lastName),
        title: nullableString(contact.title),
        email: nullableString(contact.email),
        phone: nullableString(contact.phone),
        isPrimary: contact.isPrimary === true,
        portalAccountId: nullableString(contact.portalAccountId),
        portalClaimed: contact.portalClaimed === true,
        version: nullableNumber(contact.version) ?? 0,
        createdAt: nullableString(contact.createdAt),
        updatedAt: nullableString(contact.updatedAt),
      };
    }),
    grants: source.grants.map(
      (application) =>
        normalizeGrantApplicationDetail({
          apiVersion: '1',
          application,
        }).application,
    ),
    activity: source.activity.map((value) => {
      const change = nullableObject(value);
      const actor = nullableObject(change.actor);
      return {
        id: nullableString(change.id),
        entityType: nullableString(change.entityType),
        entityId: nullableString(change.entityId),
        changeType: nullableString(change.changeType),
        previous:
          change.previous === null ? null : metadataValue(change.previous),
        current: metadataValue(change.current),
        actor: {
          userId: nullableString(actor.userId),
          email: nullableString(actor.email),
        },
        createdAt: nullableString(change.createdAt),
      };
    }),
  };
};

const normalizeOrganizationMutation = (response) => {
  const source = assertObjectResponse(response, 'organization mutation');
  if (typeof source.duplicate !== 'boolean' || !nullableString(source.status)) {
    throw new Error('The grants organization mutation response was invalid.');
  }
  return {
    status: source.status,
    duplicate: source.duplicate,
    ...normalizeGrantOrganizationWorkspace(source),
  };
};

export const getGrantCycles = async ({ signal } = {}) =>
  normalizeGrantCycles(await api.get(`${GRANTS_BASE_PATH}/cycles`, { signal }));

export const getGrantsOverview = async ({ cycleKey, signal } = {}) =>
  normalizeGrantsOverview(
    await api.get(`${GRANTS_BASE_PATH}/overview`, {
      params: cycleKey ? { cycle: cycleKey } : {},
      signal,
    }),
  );

export const getGrantOrganizations = async ({
  cycleKey,
  cursor = null,
  limit = 25,
  page = null,
  search = '',
  signal,
} = {}) => {
  const params = { limit };
  if (cycleKey) params.cycle = cycleKey;
  if (cursor) params.cursor = cursor;
  if (page) params.page = page;
  if (search.trim()) params.search = search.trim();
  return normalizeGrantOrganizations(
    await api.get(`${GRANTS_BASE_PATH}/organizations`, { params, signal }),
  );
};

export const getGrantOrganization = async (
  organizationId,
  { cycleKey, signal } = {},
) => {
  if (!organizationId) throw new Error('An organization id is required.');
  return normalizeGrantOrganizationWorkspace(
    await api.get(
      `${GRANTS_BASE_PATH}/organizations/${encodeURIComponent(organizationId)}`,
      { params: cycleKey ? { cycle: cycleKey } : {}, signal },
    ),
  );
};

export const createGrantOrganization = async ({
  cycleKey,
  idempotencyKey,
  organization,
}) =>
  normalizeOrganizationMutation(
    await api.post(
      `${GRANTS_BASE_PATH}/organizations`,
      { idempotencyKey, organization },
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
  );

export const updateGrantOrganization = async (
  organizationId,
  { cycleKey, idempotencyKey, expectedVersion, organization },
) =>
  normalizeOrganizationMutation(
    await api.patch(
      `${GRANTS_BASE_PATH}/organizations/${encodeURIComponent(organizationId)}`,
      { idempotencyKey, expectedVersion, organization },
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
  );

export const createGrantOrganizationContact = async (
  organizationId,
  { cycleKey, idempotencyKey, contact },
) =>
  normalizeOrganizationMutation(
    await api.post(
      `${GRANTS_BASE_PATH}/organizations/${encodeURIComponent(organizationId)}/contacts`,
      { idempotencyKey, contact },
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
  );

export const updateGrantOrganizationContact = async (
  organizationId,
  contactId,
  { cycleKey, idempotencyKey, expectedVersion, contact },
) =>
  normalizeOrganizationMutation(
    await api.patch(
      `${GRANTS_BASE_PATH}/organizations/${encodeURIComponent(organizationId)}/contacts/${encodeURIComponent(contactId)}`,
      { idempotencyKey, expectedVersion, contact },
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
  );

export const createGrantOrganizationRecord = async (
  organizationId,
  { cycleKey, idempotencyKey, projectName },
) =>
  normalizeOrganizationMutation(
    await api.post(
      `${GRANTS_BASE_PATH}/organizations/${encodeURIComponent(organizationId)}/grants`,
      { idempotencyKey, projectName },
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
  );

export const linkGrantOrganizationRecord = async (
  organizationId,
  { cycleKey, idempotencyKey, applicationId },
) =>
  normalizeOrganizationMutation(
    await api.post(
      `${GRANTS_BASE_PATH}/organizations/${encodeURIComponent(organizationId)}/grants/link`,
      { idempotencyKey, applicationId },
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
  );

export const updateGrantOrganizationRecord = async (
  organizationId,
  applicationId,
  { cycleKey, idempotencyKey, expectedVersion, record },
) =>
  normalizeOrganizationMutation(
    await api.patch(
      `${GRANTS_BASE_PATH}/organizations/${encodeURIComponent(organizationId)}/grants/${encodeURIComponent(applicationId)}`,
      { idempotencyKey, expectedVersion, record },
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
  );

const addWorklistParams = (params, values, keys) => {
  keys.forEach((key) => {
    const value = values?.[key];
    if (Array.isArray(value) && value.length)
      params[key] = [...value].sort().join(',');
    else if (typeof value === 'string' && value.trim())
      params[key] = value.trim();
  });
};

export const getGrantApplications = async ({
  cycleKey,
  cursor = null,
  limit = 25,
  page = null,
  search = '',
  filters = {},
  signal,
} = {}) => {
  const params = { limit };
  if (cycleKey) params.cycle = cycleKey;
  if (cursor) params.cursor = cursor;
  if (page) params.page = page;
  addWorklistParams(params, { ...filters, search }, [
    'search',
    'organizationApplicant',
    'project',
    'amountMin',
    'amountMax',
    'applicationStatus',
    'sponsorshipStatus',
    'workshopStatus',
    'questionsStatus',
    'answersStatus',
  ]);

  return normalizeGrantsApplications(
    await api.get(`${GRANTS_BASE_PATH}/applications`, { params, signal }),
  );
};

export const getGrantApplication = async (
  applicationId,
  { cycleKey, signal } = {},
) => {
  if (!applicationId) {
    throw new Error('An application id is required.');
  }

  return normalizeGrantApplicationDetail(
    await api.get(
      `${GRANTS_BASE_PATH}/applications/${encodeURIComponent(applicationId)}`,
      { params: cycleKey ? { cycle: cycleKey } : {}, signal },
    ),
  );
};

export const createGrantManualApplication = async ({
  cycleKey,
  idempotencyKey,
  profile,
  sponsorship,
  application,
}) => {
  const response = assertObjectResponse(
    await api.post(
      `${GRANTS_BASE_PATH}/applications/manual`,
      { idempotencyKey, profile, sponsorship, application },
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
    'manual application',
  );
  if (
    response.status !== 'manual_application_created' ||
    typeof response.duplicate !== 'boolean' ||
    !isObject(response.account) ||
    !isObject(response.application)
  ) {
    throw new Error('The manual application response was invalid.');
  }
  return {
    status: response.status,
    duplicate: response.duplicate,
    account: {
      id: nullableString(response.account.id),
      linkedToPortal: response.account.linkedToPortal === true,
      claimRequired: response.account.claimRequired === true,
    },
    application: normalizeGrantApplicationDetail({
      apiVersion: '1',
      application: response.application,
    }).application,
  };
};

export const createGrantStaffDocumentUpload = async (
  applicationId,
  cycleKey,
  input,
) => {
  if (!applicationId) throw new Error('An application id is required.');
  const response = assertObjectResponse(
    await api.post(
      `${GRANTS_BASE_PATH}/applications/${encodeURIComponent(applicationId)}/documents/uploads`,
      input,
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
    'staff document upload',
  );
  const file = nullableObject(response.file);
  const upload = nullableObject(response.upload);
  const url = nullableString(upload.url);
  if (
    response.status !== 'upload_ready' ||
    !nullableString(file.id) ||
    upload.method !== 'PUT' ||
    !url ||
    !isObject(upload.headers)
  ) {
    throw new Error('The staff document upload response was invalid.');
  }
  return {
    status: response.status,
    duplicate: response.duplicate === true,
    file: {
      id: nullableString(file.id),
      kind: nullableString(file.kind),
      fileName: nullableString(file.fileName),
      contentType: nullableString(file.contentType),
      sizeBytes: nullableNumber(file.sizeBytes),
    },
    upload: {
      method: upload.method,
      url,
      headers: Object.fromEntries(
        Object.entries(upload.headers).filter(
          ([name, value]) => name && typeof value === 'string',
        ),
      ),
    },
  };
};

export const completeGrantStaffDocumentUpload = async (
  applicationId,
  fileId,
  cycleKey,
) => {
  if (!applicationId || !fileId) {
    throw new Error('An application id and document file id are required.');
  }
  return assertObjectResponse(
    await api.post(
      `${GRANTS_BASE_PATH}/applications/${encodeURIComponent(applicationId)}/documents/uploads/${encodeURIComponent(fileId)}/complete`,
      {},
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
    'staff document upload completion',
  );
};

export const updateGrantWorkshop = async (
  applicationId,
  { attended, expectedVersion, idempotencyKey },
) => {
  if (!applicationId) throw new Error('An application id is required.');
  const response = assertObjectResponse(
    await api.patch(
      `${GRANTS_BASE_PATH}/applications/${encodeURIComponent(applicationId)}/workshop`,
      { attended, expectedVersion, idempotencyKey },
    ),
    'workshop update',
  );
  const workshop = nullableObject(response.workshop);
  if (
    response.status !== 'workshop_updated' ||
    typeof response.duplicate !== 'boolean' ||
    typeof workshop.attended !== 'boolean'
  ) {
    throw new Error('The workshop update response was invalid.');
  }
  return {
    status: response.status,
    duplicate: response.duplicate,
    workshop: {
      attended: workshop.attended,
      version: nullableNumber(workshop.version),
      updatedAt: nullableString(workshop.updatedAt),
    },
  };
};

export const createGrantQuestionsUpload = async (
  applicationId,
  { fileName, contentType, sizeBytes, checksumSha256, idempotencyKey },
) => {
  if (!applicationId) throw new Error('An application id is required.');
  const response = assertObjectResponse(
    await api.post(
      `${GRANTS_BASE_PATH}/applications/${encodeURIComponent(applicationId)}/questions/uploads`,
      { fileName, contentType, sizeBytes, checksumSha256, idempotencyKey },
    ),
    'questions upload',
  );
  const file = nullableObject(response.file);
  const upload = nullableObject(response.upload);
  const url = nullableString(upload.url);
  if (
    response.status !== 'upload_ready' ||
    !nullableString(file.id) ||
    upload.method !== 'PUT' ||
    !url ||
    !isObject(upload.headers)
  ) {
    throw new Error('The questions upload response was invalid.');
  }
  return {
    status: response.status,
    file: {
      id: nullableString(file.id),
      fileName: nullableString(file.fileName),
      contentType: nullableString(file.contentType),
      sizeBytes: nullableNumber(file.sizeBytes),
    },
    upload: {
      method: upload.method,
      url,
      headers: Object.fromEntries(
        Object.entries(upload.headers).filter(
          ([name, value]) => name && typeof value === 'string',
        ),
      ),
    },
  };
};

export const completeGrantQuestionsUpload = async (applicationId, fileId) => {
  if (!applicationId || !fileId) {
    throw new Error('An application id and questions file id are required.');
  }
  return assertObjectResponse(
    await api.post(
      `${GRANTS_BASE_PATH}/applications/${encodeURIComponent(applicationId)}/questions/uploads/${encodeURIComponent(fileId)}/complete`,
      {},
    ),
    'questions upload completion',
  );
};

export const createGrantAnswerDownload = async (applicationId, cycleKey) => {
  if (!applicationId) throw new Error('An application id is required.');
  return normalizeSponsorshipDownload(
    await api.post(
      `${GRANTS_BASE_PATH}/applications/${encodeURIComponent(applicationId)}/answer/download`,
      {},
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
  );
};

export const createGrantApplicationDocumentDownload = async (
  applicationId,
  fileId,
  cycleKey,
) => {
  if (!applicationId || !fileId) {
    throw new Error('An application id and document file id are required.');
  }
  return normalizeSponsorshipDownload(
    await api.post(
      `${GRANTS_BASE_PATH}/applications/${encodeURIComponent(applicationId)}/documents/${encodeURIComponent(fileId)}/download`,
      {},
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
  );
};

export const getGrantSponsorships = async ({
  cycleKey,
  cursor = null,
  limit = 25,
  page = null,
  search = '',
  filters = {},
  signal,
} = {}) => {
  const params = { limit };
  if (cycleKey) params.cycle = cycleKey;
  if (cursor) params.cursor = cursor;
  if (page) params.page = page;
  addWorklistParams(params, { ...filters, search }, [
    'search',
    'applicant',
    'coApplicant',
    'project',
    'amountMin',
    'amountMax',
    'sponsorshipType',
    'uploadedFrom',
    'uploadedBefore',
    'workflowStatus',
  ]);
  return normalizeGrantSponsorships(
    await api.get(`${GRANTS_BASE_PATH}/sponsorships`, { params, signal }),
  );
};

export const createGrantSponsorshipDownload = async (
  applicationId,
  cycleKey,
) => {
  if (!applicationId)
    throw new Error('A sponsorship application id is required.');
  return normalizeSponsorshipDownload(
    await api.post(
      `${GRANTS_BASE_PATH}/sponsorships/${encodeURIComponent(applicationId)}/download`,
      {},
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
  );
};

export const decideGrantSponsorship = async (
  applicationId,
  { decision, expectedVersion, idempotencyKey },
) => {
  if (!applicationId)
    throw new Error('A sponsorship application id is required.');
  return normalizeSponsorshipDecision(
    await api.post(
      `${GRANTS_BASE_PATH}/sponsorships/${encodeURIComponent(applicationId)}/decisions`,
      { decision, expectedVersion, idempotencyKey },
    ),
  );
};

export const createGrantSponsorshipExport = async ({
  cycleKey,
  idempotencyKey,
  search = '',
  filters = {},
} = {}) => {
  if (!idempotencyKey)
    throw new Error('A sponsorship export idempotency key is required.');
  const body = { idempotencyKey };
  addWorklistParams(body, { ...filters, search }, [
    'search',
    'applicant',
    'coApplicant',
    'project',
    'amountMin',
    'amountMax',
    'sponsorshipType',
    'uploadedFrom',
    'uploadedBefore',
    'workflowStatus',
  ]);
  const response = assertObjectResponse(
    await api.post(`${GRANTS_BASE_PATH}/sponsorships/exports`, body, {
      params: cycleKey ? { cycle: cycleKey } : {},
    }),
    'sponsorship export',
  );
  const job = nullableObject(response.export);
  const id = nullableString(job.id);
  if (response.status !== 'export_queued' || !id) {
    throw new Error('The sponsorship export response was invalid.');
  }
  return {
    status: response.status,
    export: { id, status: nullableString(job.status) },
  };
};

export const getGrantSponsorshipExport = async (exportId, cycleKey) => {
  if (!exportId) throw new Error('A sponsorship export id is required.');
  const response = assertObjectResponse(
    await api.post(
      `${GRANTS_BASE_PATH}/sponsorships/exports/${encodeURIComponent(exportId)}/status`,
      {},
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
    'sponsorship export status',
  );
  const job = nullableObject(response.export);
  const download = nullableObject(job.download);
  if (response.status !== 'export_status' || !nullableString(job.id)) {
    throw new Error('The sponsorship export status response was invalid.');
  }
  return {
    status: response.status,
    export: {
      id: nullableString(job.id),
      status: nullableString(job.status),
      fileName: nullableString(job.fileName),
      rowCount: nullableNumber(job.rowCount),
      download: job.download
        ? {
            url: nullableString(download.url),
            expiresAt: nullableString(download.expiresAt),
          }
        : null,
    },
  };
};

export const createGrantApplicantExport = async ({
  cycleKey,
  idempotencyKey,
  search = '',
  filters = {},
} = {}) => {
  if (!idempotencyKey)
    throw new Error('An applicant export idempotency key is required.');
  const body = { idempotencyKey };
  addWorklistParams(body, { ...filters, search }, [
    'search',
    'organizationApplicant',
    'project',
    'amountMin',
    'amountMax',
    'applicationStatus',
    'sponsorshipStatus',
    'workshopStatus',
    'questionsStatus',
    'answersStatus',
  ]);
  const response = assertObjectResponse(
    await api.post(`${GRANTS_BASE_PATH}/applications/exports`, body, {
      params: cycleKey ? { cycle: cycleKey } : {},
    }),
    'applicant export',
  );
  const job = nullableObject(response.export);
  const id = nullableString(job.id);
  if (response.status !== 'export_queued' || !id) {
    throw new Error('The applicant export response was invalid.');
  }
  return {
    status: response.status,
    export: { id, status: nullableString(job.status) },
  };
};

export const getGrantApplicantExport = async (exportId, cycleKey) => {
  if (!exportId) throw new Error('An applicant export id is required.');
  const response = assertObjectResponse(
    await api.post(
      `${GRANTS_BASE_PATH}/applications/exports/${encodeURIComponent(exportId)}/status`,
      {},
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
    'applicant export status',
  );
  const job = nullableObject(response.export);
  const download = nullableObject(job.download);
  if (response.status !== 'export_status' || !nullableString(job.id)) {
    throw new Error('The applicant export status response was invalid.');
  }
  return {
    status: response.status,
    export: {
      id: nullableString(job.id),
      status: nullableString(job.status),
      fileName: nullableString(job.fileName),
      rowCount: nullableNumber(job.rowCount),
      download: job.download
        ? {
            url: nullableString(download.url),
            expiresAt: nullableString(download.expiresAt),
          }
        : null,
    },
  };
};

export const getGrantNotificationsUnread = async (config = {}) =>
  normalizeGrantNotificationStatus(
    await api.get(`${GRANTS_BASE_PATH}/notifications/unread`, config),
  );

export const markGrantNotificationsRead = async (config = {}) =>
  normalizeGrantNotificationStatus(
    await api.patch(`${GRANTS_BASE_PATH}/notifications/read`, {}, config),
    false,
  );

const createGrantPackage = async ({
  path,
  label,
  cycleKey,
  idempotencyKey,
}) => {
  if (!idempotencyKey)
    throw new Error(`A ${label} idempotency key is required.`);
  const response = assertObjectResponse(
    await api.post(
      `${GRANTS_BASE_PATH}/${path}`,
      { idempotencyKey },
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
    label,
  );
  const job = nullableObject(response.export);
  const id = nullableString(job.id);
  if (response.status !== 'export_queued' || !id) {
    throw new Error(`The ${label} response was invalid.`);
  }
  return {
    status: response.status,
    export: { id, status: nullableString(job.status) },
  };
};

const getGrantPackage = async ({ path, label, exportId, cycleKey }) => {
  if (!exportId) throw new Error(`A ${label} export id is required.`);
  const response = assertObjectResponse(
    await api.post(
      `${GRANTS_BASE_PATH}/${path}/${encodeURIComponent(exportId)}/status`,
      {},
      { params: cycleKey ? { cycle: cycleKey } : {} },
    ),
    `${label} status`,
  );
  const job = nullableObject(response.export);
  const progress = nullableObject(job.progress);
  const download = nullableObject(job.download);
  if (response.status !== 'export_status' || !nullableString(job.id)) {
    throw new Error(`The ${label} status response was invalid.`);
  }
  if (job.isCurrent !== null && typeof job.isCurrent !== 'boolean') {
    throw new Error(`The ${label} freshness response was invalid.`);
  }
  if (job.status === 'completed' && typeof job.isCurrent !== 'boolean') {
    throw new Error(`The completed ${label} freshness response was invalid.`);
  }
  if (job.isCurrent === false && job.download !== null) {
    throw new Error(`A stale ${label} cannot be downloaded.`);
  }
  if (job.isCurrent === true && job.download === null) {
    throw new Error(`The current ${label} download was missing.`);
  }
  return {
    status: response.status,
    export: {
      id: nullableString(job.id),
      status: nullableString(job.status),
      fileName: nullableString(job.fileName),
      rowCount: nullableNumber(job.rowCount),
      createdAt: nullableString(job.createdAt),
      completedAt: nullableString(job.completedAt),
      isCurrent: job.isCurrent,
      progress: {
        stage: nullableString(progress.stage),
        filesTotal: nullableNumber(progress.filesTotal),
        filesProcessed: nullableNumber(progress.filesProcessed),
        bytesTotal: nullableNumber(progress.bytesTotal),
        bytesProcessed: nullableNumber(progress.bytesProcessed),
      },
      download: job.download
        ? {
            url: nullableString(download.url),
            expiresAt: nullableString(download.expiresAt),
          }
        : null,
    },
  };
};

export const createGrantCyclePackage = ({ cycleKey, idempotencyKey } = {}) =>
  createGrantPackage({
    path: 'overview/exports',
    label: 'cycle package',
    cycleKey,
    idempotencyKey,
  });

export const getGrantCyclePackage = (exportId, cycleKey) =>
  getGrantPackage({
    path: 'overview/exports',
    label: 'cycle package',
    exportId,
    cycleKey,
  });

export const createGrantSponsorshipPackage = ({
  cycleKey,
  idempotencyKey,
} = {}) =>
  createGrantPackage({
    path: 'sponsorships/package-exports',
    label: 'sponsorship package',
    cycleKey,
    idempotencyKey,
  });

export const getGrantSponsorshipPackage = (exportId, cycleKey) =>
  getGrantPackage({
    path: 'sponsorships/package-exports',
    label: 'sponsorship package',
    exportId,
    cycleKey,
  });

export const createGrantApplicantPackage = ({
  cycleKey,
  idempotencyKey,
} = {}) =>
  createGrantPackage({
    path: 'applications/package-exports',
    label: 'applicant package',
    cycleKey,
    idempotencyKey,
  });

export const getGrantApplicantPackage = (exportId, cycleKey) =>
  getGrantPackage({
    path: 'applications/package-exports',
    label: 'applicant package',
    exportId,
    cycleKey,
  });
